import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, Mail, DollarSign, Phone } from "lucide-react";

const STEPS = [
  { n: "1", title: "Text or DM your order", body: "Send us the juices you want and how many. Easy as that." },
  { n: "2", title: "Pay with Cash App", body: "Send payment to $HollyJuices to confirm your batch." },
  { n: "3", title: "Pickup or delivery", body: "We'll message you the moment your fresh juice is ready." },
];

const HowToOrder = () => {
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  return (
    <section id="order" className="relative py-20 sm:py-28 scroll-mt-20" data-testid="how-to-order-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-[32px] bg-gradient-to-br from-[#FFC93C] to-[#FF7A1A] p-8 sm:p-14 relative overflow-hidden shadow-[0_24px_50px_rgba(255,122,26,0.28)]"
        >
          <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/15" />
          <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-[#FF3D68]/20" />

          <div className="relative z-10">
            <span className="font-display font-extrabold text-white/80 text-sm tracking-[0.2em] uppercase">02 — How to Order</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-white max-w-xl">
              No app, no cart. Just a text away.
            </h2>

            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="bg-white/95 backdrop-blur rounded-2xl p-6"
                >
                  <div className="w-11 h-11 rounded-full bg-[#1A1A1A] text-white font-display font-extrabold text-lg flex items-center justify-center mb-4">
                    {s.n}
                  </div>
                  <h3 className="font-display font-bold text-lg text-[#1A1A1A]">{s.title}</h3>
                  <p className="mt-1.5 text-sm text-[#1A1A1A]/70 leading-relaxed">{s.body}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => copy("$HollyJuices", "Cash App handle")}
                className="flex-1 flex items-center justify-between gap-4 bg-[#1A1A1A] rounded-2xl px-6 py-5 text-left hover:scale-[1.02] transition-transform active:scale-95"
                data-testid="copy-cashapp-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#7ED321] flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-white/50 font-semibold uppercase tracking-wide">Cash App</p>
                    <p className="font-display font-extrabold text-xl text-white">$HollyJuices</p>
                  </div>
                </div>
                <span className="text-xs font-bold text-[#FFC93C] uppercase">Tap to copy</span>
              </button>

              <a
                href="mailto:HollyJuicesLLC@gmail.com"
                className="flex-1 flex items-center gap-3 bg-white rounded-2xl px-6 py-5 hover:scale-[1.02] transition-transform"
                data-testid="email-order-link"
              >
                <div className="w-11 h-11 rounded-xl bg-[#FF3D68] flex items-center justify-center">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-[#1A1A1A]/50 font-semibold uppercase tracking-wide">Email us</p>
                  <p className="font-display font-bold text-base text-[#1A1A1A] truncate">HollyJuicesLLC@gmail.com</p>
                </div>
              </a>

              <a
                href="tel:+18447814221"
                className="flex-1 flex items-center gap-3 bg-white rounded-2xl px-6 py-5 hover:scale-[1.02] transition-transform"
                data-testid="phone-order-link"
              >
                <div className="w-11 h-11 rounded-xl bg-[#8B5CF6] flex items-center justify-center">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-[#1A1A1A]/50 font-semibold uppercase tracking-wide">Text or call</p>
                  <p className="font-display font-bold text-base text-[#1A1A1A] truncate">844-781-4221</p>
                </div>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowToOrder;
